// Checkout state shared by the two POS steps (/pos and /pos/cart).
//
// Discount, tax, fulfilment, customer and the chosen promos used to be local
// state of the POS route; the review step is its own route, so they live here.
// In memory only, like the cart: a reload starts a clean sale.

import { create } from 'zustand';
import type { Customer, DiscountType, FulfillmentType, PaymentTerms } from '@/types/domain';

export const DEFAULT_TAX_RATE = 11;

export interface PosCheckoutState {
  discountType: DiscountType;
  discountValue: number;
  taxEnabled: boolean;
  taxRate: number;
  fulfillment: FulfillmentType;
  customer: Customer | null;
  /** Promo ids applied, in the order chosen. */
  promoIds: string[];
  /** The draft this cart was loaded from, if any — deleted once the sale lands. */
  loadedDraftId: string | null;
  /** The last completed sale, for the toast on the product step. */
  lastSuccess: { id: string; invoiceNumber: string; paymentTerms: PaymentTerms } | null;

  setDiscount: (type: DiscountType, value: number) => void;
  setTaxEnabled: (on: boolean) => void;
  setFulfillment: (f: FulfillmentType) => void;
  setCustomer: (c: Customer | null) => void;
  togglePromo: (id: string) => void;
  setPromoIds: (ids: string[]) => void;
  setLoadedDraftId: (id: string | null) => void;
  setLastSuccess: (s: PosCheckoutState['lastSuccess']) => void;
  /** Back to a clean sale (after charging, saving a draft, or ending a shift). */
  resetSale: () => void;
}

const CLEAN = {
  discountType: null as DiscountType,
  discountValue: 0,
  taxEnabled: true,
  taxRate: DEFAULT_TAX_RATE,
  fulfillment: 'now' as FulfillmentType,
  customer: null as Customer | null,
  promoIds: [] as string[],
  loadedDraftId: null as string | null,
};

export const usePosCheckout = create<PosCheckoutState>((set) => ({
  ...CLEAN,
  lastSuccess: null,
  setDiscount: (discountType, discountValue) => set({ discountType, discountValue }),
  setTaxEnabled: (taxEnabled) => set({ taxEnabled }),
  setFulfillment: (fulfillment) => set({ fulfillment }),
  setCustomer: (customer) => set({ customer }),
  togglePromo: (id) =>
    set((s) => ({ promoIds: s.promoIds.includes(id) ? s.promoIds.filter((x) => x !== id) : [...s.promoIds, id] })),
  setPromoIds: (promoIds) => set({ promoIds }),
  setLoadedDraftId: (loadedDraftId) => set({ loadedDraftId }),
  setLastSuccess: (lastSuccess) => set({ lastSuccess }),
  resetSale: () => set({ ...CLEAN }),
}));
